import gettext from '@sdk/lang/gettext';
import { FormProvider } from 'react-hook-form';
import { ModalState } from '@sdk/components/ui/modal/useModalState';
import { FormLabel, FormLayout } from '@sdk/components/ui/modalForm/modalForm.styles';
import TextInputBase from '@sdk/components/forms/TextInputBase';
import { DismissButton } from '@sdk/components/ui/InfoModal/infoInnerModal.styles';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import Tooltip from '@sdk/common/tooltip/Tooltip';
import Row from '@sdk/components/ui/spacing/Row';
import noop from '@sdk/utils/noop';

import { TaskInstance } from '@web/shiftTasks/types/TaskInstance';
import BodyPortal from '@web/components/BodyPortal';
import { TaskEditModal } from '@web/shiftTasks/ComposeTask/composeTask.styles';
import ShiftTaskIcon from '@web/assets/svg/Icons/32/shift-blue.svg';
import { TitleField } from '@web/shiftTasks/ComposeTask/TitleField/TitleField';
import { AttachmentsField } from '@web/shiftTasks/ComposeTask/AttachmentsField/AttachmentsField';
import { DescriptionField } from '@web/shiftTasks/ComposeTask/DescriptionField/DescriptionField';
import CalendarIcon from '@web/assets/svg/Icons/32/date-gray.svg';
import { getInstanceShiftLabel } from '@web/shiftTasks/EditTaskInstance/editInstance.utils';
import { SubtasksField } from '@web/shiftTasks/ComposeTask/Subtasks/SubtasksField/SubtasksField';

import { useEditTaskInstanceForm } from './editTaskInstance.hooks';
import { ShiftInfoContainer } from './editInstance.styles';

type Props = {
  modalState: ModalState;
  originalInstance: TaskInstance;
  usTimeFormat: boolean; // usTimeFormat and personasDict are temporary to avoid fetching session just for this modal
};

export const EditTaskInstance = ({ modalState, originalInstance, usTimeFormat }: Props) => {
  const { areAttachmentsEnabled, formMethods, startDate, onSubmit } = useEditTaskInstanceForm(
    originalInstance,
    modalState.close
  );

  const {
    formState: { isSubmitting, isValid, isDirty },
  } = formMethods;
  const isSubmitActive = isDirty && isValid;
  const tooltipText = gettext('This field is not editable. To make edits, go to schedule view.');

  return (
    <BodyPortal>
      <BodyPortal>
        <TaskEditModal
          titleText={gettext('Edit task')}
          titleIcon={<ShiftTaskIcon />}
          dismiss={modalState.close}
          buttons={
            <>
              <DismissButton onClick={modalState.close}>{gettext('Cancel')}</DismissButton>
              <DismissButton
                buttonType={isValid ? ButtonType.primary : ButtonType.disabledStyle}
                onClick={!isSubmitting ? onSubmit : noop}
                inactive={!isSubmitActive || isSubmitting}>
                {gettext('Save')}
              </DismissButton>
            </>
          }>
          <FormProvider {...formMethods}>
            <form>
              <FormLayout>
                <>
                  <FormLabel>
                    {gettext('Date')} <CalendarIcon aria-hidden={true} />
                  </FormLabel>
                  <Row data-tip={tooltipText} data-place="right">
                    <TextInputBase disabled value={startDate} />
                  </Row>
                </>

                <>
                  <FormLabel>
                    {gettext('Shift')} <CalendarIcon aria-hidden={true} />
                  </FormLabel>
                  <ShiftInfoContainer data-tip={tooltipText} data-place="right">
                    <TextInputBase
                      disabled
                      value={getInstanceShiftLabel(originalInstance, usTimeFormat)}
                    />
                  </ShiftInfoContainer>
                </>

                <TitleField />
                <DescriptionField />
                <SubtasksField />
                {areAttachmentsEnabled && <AttachmentsField />}
              </FormLayout>
            </form>
          </FormProvider>

          <Tooltip />
        </TaskEditModal>
      </BodyPortal>
    </BodyPortal>
  );
};
